import { useCallback, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

// Not "react-intl": v2 has no `useIntl`, and the design system's precompiled AST
// `defaultMessage`s crash its formatter. See src/shims/react-intl.js.
import {
  PhoneNumberFormFields,
  PhoneVerificationModal,
} from "@ps-design-system/components/auth/PhoneVerification";

import { Profile } from "api/api-profile";
import { useIntl } from "react-intl-compat";

import { updateProfile, verifyPhone } from "../app-main/actions/profile";
import { selectAuthAccessToken } from "../app-main/selectors/auth.selectors";
import {
  selectExtUserId,
  selectProfilePhoneE164,
} from "../app-main/selectors/profile.selectors";

type EditVerifyPhoneModalProps = {
  open: boolean;
  startingStep?: "phone" | "verify";
  variant?: "default" | "marketing";
  onOpenChange: (open: boolean) => void;
};

export const EditVerifyPhoneModal = ({
  open,
  startingStep,
  variant = "default",
  onOpenChange,
}: EditVerifyPhoneModalProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const accessToken = useSelector(selectAuthAccessToken);
  const isUserFAEnabled = useSelector(selectExtUserId);
  const shouldUseNewEndpoints = Boolean(isUserFAEnabled);
  const [verificationId, setVerificationId] = useState<string | undefined>();

  const phoneE164 = useSelector(selectProfilePhoneE164);

  // These are callback-based APIs, so the failure has to be turned into a
  // rejection - the design system only renders an error if the promise it
  // awaits rejects. Rejecting with the raw response lets it match on
  // `error` for the quota-specific message.
  const handleSendVerificationCode = useCallback(
    async () =>
      new Promise<void>((resolve, reject) => {
        const handleResponse = (response: {
          statusCode: number;
          verification_id?: string;
          error?: string;
        }) => {
          if (response.statusCode !== 200 || response.error) {
            reject(response);
            return;
          }
          if (shouldUseNewEndpoints) {
            setVerificationId(response.verification_id);
          }
          resolve();
        };

        if (shouldUseNewEndpoints) {
          Profile.fetchPhoneVerifyCode(accessToken, handleResponse);
        } else {
          Profile.fetchPhoneVerifyCodeOld(accessToken, handleResponse);
        }
      }),
    [accessToken, shouldUseNewEndpoints],
  );

  const handleSubmitPhoneNumber = async (
    phoneNumberFormData: PhoneNumberFormFields,
  ) => {
    // only update the phone if it changed
    if (phoneE164 !== phoneNumberFormData.phone) {
      const payload = shouldUseNewEndpoints
        ? {
            phone: {
              country_code: phoneNumberFormData.phone_country,
              number: phoneNumberFormData.phone_number,
            },
          }
        : {
            phone: phoneNumberFormData.phone_number,
            phone_country: phoneNumberFormData.phone_country,
            phone_country_code: phoneNumberFormData.phone_country_code,
          };

      await dispatch(updateProfile(accessToken, payload));

      await handleSendVerificationCode();
    }
  };

  const handleOnSubmitVerificationCode = async ({
    verificationCode,
  }: {
    verificationCode: string;
  }) => {
    await dispatch(verifyPhone(verificationCode, verificationId));
  };

  return (
    <PhoneVerificationModal
      defaultPhoneNumber={phoneE164}
      intl={intl}
      open={open}
      setOpen={onOpenChange}
      startingStep={startingStep}
      variant={variant}
      verificationCodeLength={shouldUseNewEndpoints ? 6 : 4}
      onSubmitPhoneNumber={handleSubmitPhoneNumber}
      onSubmitVerificationCode={handleOnSubmitVerificationCode}
      resendVerificationCode={handleSendVerificationCode}
    />
  );
};
